/**
 * OUR API SESSION — challenge → sign → bearer, single-flight, `sessionStorage`.
 *
 * DISTINCT FROM THE SIGNING SESSION, and the distinction is the custody model:
 *   the SIGNING session lets this browser SIGN. Its key is non-extractable, in the SDK's keystore, and our
 *     server never sees it.
 *   THIS session tells our backend WHICH WALLET IS CALLING, so every money route takes the wallet from the
 *     session instead of from a request body. It is obtained by USING the first — the user signs a nonce —
 *     so our server learns who they are without ever holding anything that can sign for them.
 *
 * WHY `sessionStorage` AND NOT `localStorage`: a bearer that outlives the browsing session it was issued for
 * is a credential sitting on disk for the next person at the machine, and it is readable by any script on the
 * origin either way. Losing it costs ONE signature to re-obtain, which is not worth trading for the wider
 * exposure. It is never logged, never beaconed and never put in a toast.
 *
 * WHAT THIS FILE REFUSES TO DO, because it is the easy shape and a silent one: return `null` on failure so the caller spreads
 * `{}` into the headers and proceeds UNAUTHENTICATED. `api/http.ts` throws `no_session` before a request
 * leaves, and `signIn()` below surfaces a real error. There is no silent downgrade on a money surface.
 */

import { isApiError } from "../api/errors.ts";
import { setOwnerProvider, setSessionProvider } from "../api/http.ts";
import * as api from "../api/trade.ts";
import { nowMs } from "../lib/time.ts";
import {
  type SignedOutReason,
  sessionStore,
  setSignedIn,
  setSignedOut,
  setSignerState,
} from "../stores/session.ts";
import type { Signer } from "../wallet/signer.ts";
import { signerExpiresAt } from "./turnkey.ts";

const TOKEN_KEY = "kinetiq.v3.session";

/** A token that expires MID-REQUEST fails the request, and the retry costs a signing prompt the user did not
 *  ask for. Treat it as dead a minute early and re-mint on our own schedule instead of theirs. */
const EXPIRY_SKEW_MS = 60_000;

interface StoredToken {
  token: string;
  address: string;
  expiresAt: number;
  /** NOT on the wire — carried here from the wallet record so a signature can be requested without a second
   *  lookup. The server neither sends nor needs it.
   *
   *  ⚠️ NULL FOR AN EXTERNAL WALLET, which has no sub-organization: the key lives in an extension, not with
   *  turnkey. That is a real state, not a missing value, and it is why "can this sign?" is `signerFor` and
   *  not a null check on this field. */
  subOrgId: string | null;
}

let cached: StoredToken | null = null;
let inflight: Promise<string> | null = null;

function read(): StoredToken | null {
  if (cached) return cached;
  try {
    const raw = globalThis.sessionStorage?.getItem(TOKEN_KEY);
    if (!raw) return null;
    cached = JSON.parse(raw) as StoredToken;
    return cached;
  } catch {
    // A corrupt entry is not worth surfacing — it is indistinguishable from signed out, and it recovers by
    // signing in.
    return null;
  }
}

function write(t: StoredToken | null): void {
  cached = t;
  try {
    if (t) globalThis.sessionStorage?.setItem(TOKEN_KEY, JSON.stringify(t));
    else globalThis.sessionStorage?.removeItem(TOKEN_KEY);
  } catch {
    // Private-browsing modes refuse storage. The in-memory copy still serves this tab, which is the whole
    // scope of a per-tab bearer anyway.
  }
}

function live(t: StoredToken | null): t is StoredToken {
  return t !== null && t.expiresAt - EXPIRY_SKEW_MS > nowMs();
}

/**
 * THE PROVIDER `api/http.ts` READS. Synchronous by contract: the wrapper attaches a bearer or refuses, and it
 * never awaits a signing prompt in the middle of a request the user thinks is already in flight.
 *
 * Installed once, at module load, so no call site can forget it and no second copy of "where is the token"
 * can exist.
 */
setSessionProvider(() => {
  const t = read();
  return live(t) ? t.token : null;
});

/** THE SAME STATE, THE SAME LIFETIME, INSTALLED IN THE SAME PLACE — see `setOwnerProvider`. The trader
 *  surface names the wallet in the request rather than deriving it from the bearer, and the two answers come
 *  from one record so they can never disagree about who is signed in. */
setOwnerProvider(() => {
  const t = read();
  return live(t) ? t.address : null;
});

/** For the optimistic first paint: is there a bearer at all, without validating it? */
export function hasStoredBearer(): boolean {
  return live(read());
}

export function storedAddress(): string | null {
  const t = read();
  return live(t) ? t.address : null;
}

/**
 * SIGN IN — challenge → sign → bearer. SINGLE-FLIGHTED.
 *
 * Several components each noticing "no session" must not each trigger their own signing prompt: the user sees
 * ONE prompt or none. Without this the trade panel, the balance watcher and the deposit panel each open one on
 * a cold load, and the second and third are prompts for a signature the app already has.
 *
 * Throws on failure — a caller decides what that costs. It never returns `null`.
 */
export async function signIn(signer: Signer): Promise<string> {
  const address = signer.address;
  const subOrgId = signer.subOrgId;
  const existing = read();
  if (live(existing) && existing.address === address) return existing.token;
  if (inflight) return inflight;

  inflight = (async () => {
    const challenge = await api.sessionChallenge(address);
    // The message is the SERVER's, rendered in the prompt verbatim. It is human-readable and states that it
    // cannot move funds — a prompt showing an opaque blob trains users to approve opaque blobs.
    const signature = await signer.signMessage(challenge.message);
    // THE NONCE IS ECHOED BACK. The server consumes it on presentation, pass or fail, so a replay has nothing
    // to redeem and there is no oracle to grind against.
    const grant = await api.createSession({ address, nonce: challenge.nonce, signature });
    write({
      token: grant.token,
      address: grant.address,
      subOrgId,
      expiresAt: grant.expiresAt,
    });
    setSignedIn({ address: grant.address, subOrgId, expiresAt: grant.expiresAt });
    setSignerState(true); // we just signed with it; a probe would say the same one round trip later
    return grant.token;
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

/**
 * VERIFY a stored bearer against the server, and settle the top bar.
 *
 * Called once on load (only when a bearer exists) and again on `visibilitychange:visible`. The second call is
 * NOT decoration: both the balance poll and the quote refresh are visibility-gated, so a backgrounded tab
 * issues NOTHING for an hour and is idle-expired while still rendering as signed in — and the user discovers
 * it by pressing Buy. One cheap indexed lookup on return removes that entire class.
 */
export async function verifySession(signal?: AbortSignal): Promise<boolean> {
  const t = read();
  if (!live(t)) {
    // An absent or expired-by-our-own-clock bearer needs no round trip to disprove.
    setSignedOut(t === null ? "never" : "expired");
    return false;
  }
  try {
    const state = await api.whoAmI(signal);
    write({ ...t, expiresAt: state.expiresAt });
    setSignedIn({ address: state.address, subOrgId: t.subOrgId, expiresAt: state.expiresAt });
    return true;
  } catch (e) {
    // A NETWORK failure is not a sign-out. "Could not reach the server" and "your session ended" have
    // different fixes, and treating the first as the second logs people out during a deploy.
    if (isApiError(e) && e.status === 401) {
      onUnauthorized(reasonFromCode(e.code));
      return false;
    }
    return sessionStore.get().status === "signedIn";
  }
}

/**
 * Map the server's own 401 code to the reason the user is shown. An unrecognised code is `expired` rather than
 * a guess with a specific cause attached — the honest default is the commonest one.
 *
 * ⚠️ **THE SERVER SENDS ONE CODE FOR ALL OF THEM.** `TRADE_ERROR_CODES` collapses every sign-in and session
 * failure into `unauthorized` — deliberately, so a caller cannot enumerate which of "expired", "idle",
 * "revoked" or "never existed" applies to an address. That is the right call for an unauthenticated prober
 * and it costs the SIGNED-IN user the specific sentence they need, because all four have different fixes.
 *
 * So this maps what it can and defaults to `expired`, which is the commonest cause and the one whose remedy
 * ("sign in again") is correct for all four. **Recovering the distinction needs a non-enumerable reason field
 * on the 401 for a request that PRESENTED a token** — a server change, filed in this app's `todo.md`. Guessing
 * a specific cause here would be worse than the generic one.
 */
function reasonFromCode(code: string): SignedOutReason {
  if (code === "idle") return "idle";
  if (code === "revoked") return "revoked";
  return "expired";
}

/** The wallet's sub-organization, needed to ask for a signature and NOT carried on the wire. */
export function storedSubOrgId(): string | null {
  const t = read();
  return live(t) ? t.subOrgId : null;
}

/**
 * THE ONE PLACE A 401 BECOMES A SIGNED-OUT STATE.
 *
 * Every money call routes its 401 here so the transition happens once, with a named reason, and so the
 * PANEL'S AMOUNT IS NEVER TOUCHED by it — a cleared field is how a user concludes nothing happened and enters
 * the trade a second time. The bearer is dropped; the wallet record is not, because re-signing-in needs it
 * and re-creating an account costs a metered vendor operation.
 */
export function onUnauthorized(reason: SignedOutReason = "expired"): void {
  write(null);
  setSignedOut(reason);
}

/** True if this error is a session ending, so a caller can branch without knowing the taxonomy. */
export function isSessionExpiry(e: unknown): boolean {
  return isApiError(e) && (e.status === 401 || e.code === "no_session");
}

/**
 * SIGN OUT — all three credentials, IN ORDER, and the order is load-bearing.
 *
 * Clearing the wallet record while leaving a session in the keystore leaves a session bound to a key the app
 * can no longer find, and every later signing attempt fails with "Key not found" — a state the user cannot
 * escape without clearing site data. So: tell the server (best effort), clear the SIGNING session, clear the
 * bearer, then forget the record.
 *
 * `clearSigning` is injected rather than imported so this module does not pull the signing SDK into the
 * entry chunk; `useAuth` passes the real one.
 */
export async function signOutEverywhere(clearSigning: () => Promise<void>): Promise<void> {
  // Best effort, and a failure does not block the local clear: the user asked to leave, and a server we
  // cannot reach must not keep them signed in on this device.
  try {
    if (live(read())) await api.destroySession();
  } catch {
    /* deliberately swallowed */
  }
  await clearSigning();
  write(null);
  setSignedOut("signed_out", true);
  setSignerState(false);
}
